import { createContext, useState, useContext, useEffect } from 'react'
import { getUsernameFromToken, getIsStaffFromToken, logout as authLogout, isAuthenticated as checkIsAuthenticated } from '../hooks/useAuth'
import { useRouter } from 'next/router'

const AuthContext = createContext({
  isAuthenticated: false,
  username: 'Guest',
  authToken: null,
  isStaff: false,
  login: () => {},
  logout: () => {}
})

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [username, setUsername] = useState('Guest')
  const [authToken, setAuthToken] = useState(null)
  const [isStaff, setIsStaff] = useState(false)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  // Check authentication status on initial load
  useEffect(() => {
    // Only run on client-side
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token')
      const user = getUsernameFromToken()
      const authenticated = checkIsAuthenticated()
      
      // Debug logging
      console.log('Auth Context Init Debug:', {
        hasToken: !!token,
        tokenLength: token?.length,
        user,
        authenticated,
        tokenPreview: token ? token.substring(0, 50) + '...' : 'none'
      })
      
      if (user && authenticated && token) {
        console.log('Setting authenticated state for user:', user)
        setUsername(user)
        setIsAuthenticated(true)
        setAuthToken(token)
        // Check if user is staff
        const staffStatus = getIsStaffFromToken()
        setIsStaff(staffStatus)
        console.log('User staff status:', staffStatus)
      } else {
        console.log('Clearing authentication state - checks failed:', { user, authenticated, hasToken: !!token })
        // Clear any stale data if authentication check fails
        setIsAuthenticated(false)
        setAuthToken(null)
        setUsername('Guest')
        setIsStaff(false)
      }
      setLoading(false)
    }
  }, [])

  const login = (user) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token')
      setUsername(user || 'User')
      setIsAuthenticated(true)
      setAuthToken(token)
      setLoading(false)
      // Check if user is staff
      const staffStatus = getIsStaffFromToken()
      setIsStaff(staffStatus)
      console.log('Login - User staff status:', staffStatus)
    }
  }

  const logout = () => {
    // Optimized logout - set states first to prevent components from making API calls
    setIsAuthenticated(false)
    setAuthToken(null)
    setUsername('Guest')
    setIsStaff(false)
    
    // Then clear localStorage
    authLogout()
    
    // Only navigate if not on home page
    if (router.pathname !== '/') {
      router.push('/')
    }
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, username, authToken, isStaff, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
export { AuthContext }
